import {
  Building2,
  Check,
  Receipt,
  RefreshCw,
  TrendingUp,
  Users2,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Audience } from "@/components/auth/AudienceToggle";

interface AuthBrandColumnProps {
  mode: "login" | "register";
  audience?: Audience;
}

/**
 * Right column for the auth shell. Swaps content along two axes:
 * - mode: login (activity ticker) vs. register (testimonial + value props)
 * - audience: business (existing) vs. accountant (portfolio framing)
 */
export function AuthBrandColumn({
  mode,
  audience = "business",
}: AuthBrandColumnProps): JSX.Element {
  const { t } = useTranslation();

  if (mode === "login" && audience === "accountant") {
    return (
      <div className="fx-auth-brand-col">
        <div className="fx-auth-brand-inner">
          <div>
            <span className="fx-auth-brand-eyebrow">
              <span className="dot" />
              {t(
                "auth.brandColumn.login.accountant.eyebrow",
                "Tu portafolio · esta semana",
              )}
            </span>
            <p className="fx-auth-brand-quote">
              <span className="accent">
                {t(
                  "auth.brandColumn.login.accountant.quoteAccent1",
                  "12 clientes",
                )}
              </span>{" "}
              {t(
                "auth.brandColumn.login.accountant.quoteMid",
                "presentaron IVA,",
              )}
              <br />
              <span className="accent">
                {t(
                  "auth.brandColumn.login.accountant.quoteAccent2",
                  "3 vencimientos",
                )}
              </span>{" "}
              {t(
                "auth.brandColumn.login.accountant.quoteTail",
                "fueron resueltos por Fiscalo.",
              )}
            </p>
          </div>

          <div className="fx-auth-panel-mini">
            <div className="header">
              <span>
                {t(
                  "auth.brandColumn.login.accountant.clientsTitle",
                  "Tus clientes",
                )}
              </span>
              <span style={{ color: "var(--fx-accent-300)" }}>
                {t(
                  "auth.brandColumn.login.accountant.clientsAlertCount",
                  "3 con alertas",
                )}
              </span>
            </div>
            <ul>
              <li>
                <span className="ico">
                  <Building2 aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.login.accountant.client1",
                    "Salkantai SAS · IVA listo · revisar",
                  )}
                </span>
                <span className="when">
                  {t("auth.brandColumn.login.accountant.client1When", "hoy")}
                </span>
              </li>
              <li>
                <span className="ico">
                  <Check aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.login.accountant.client2",
                    "Café Brasil · libros del mes",
                  )}
                </span>
                <span className="when">
                  {t("auth.brandColumn.login.accountant.client2When", "ayer")}
                </span>
              </li>
              <li>
                <span className="ico">
                  <RefreshCw aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.login.accountant.client3",
                    "Sastre Pereira · onboarding al 80%",
                  )}
                </span>
                <span className="when">
                  {t(
                    "auth.brandColumn.login.accountant.client3When",
                    "hace 2h",
                  )}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "register" && audience === "accountant") {
    return (
      <div className="fx-auth-brand-col">
        <div className="fx-auth-brand-inner">
          <div>
            <span className="fx-auth-brand-eyebrow">
              <span className="dot" />
              {t(
                "auth.brandColumn.register.accountant.eyebrow",
                "Para contadores · solo o con estudio",
              )}
            </span>
            <p className="fx-auth-brand-quote">
              {t(
                "auth.brandColumn.register.accountant.quoteLead",
                "Tu cartera entera",
              )}{" "}
              <span className="accent">
                {t(
                  "auth.brandColumn.register.accountant.quoteAccent1",
                  "en un solo panel.",
                )}
              </span>
              <br />
              {t(
                "auth.brandColumn.register.accountant.quoteTail",
                "Libros y vencimientos,",
              )}{" "}
              <span className="accent">
                {t(
                  "auth.brandColumn.register.accountant.quoteAccent2",
                  "hechos antes que abras DGI.",
                )}
              </span>
            </p>
          </div>

          <div className="fx-auth-panel-mini">
            <div className="header">
              <span>
                {t(
                  "auth.brandColumn.register.accountant.planTitle",
                  "Lo que incluye",
                )}
              </span>
              <span style={{ color: "var(--fx-accent-300)" }}>
                {t(
                  "auth.brandColumn.register.accountant.planTrial",
                  "Gratis",
                )}
              </span>
            </div>
            <ul>
              <li>
                <span className="ico">
                  <Users2 aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.register.accountant.plan1",
                    "Empresas ilimitadas",
                  )}
                </span>
                <span className="when" />
              </li>
              <li>
                <span className="ico">
                  <TrendingUp aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.register.accountant.plan2",
                    "Panel consolidado · vencimientos y alertas",
                  )}
                </span>
                <span className="when" />
              </li>
              <li>
                <span className="ico">
                  <Check aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.register.accountant.plan3",
                    "Cada cliente · 15 min/mes menos en libros",
                  )}
                </span>
                <span className="when" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "login") {
    return (
      <div className="fx-auth-brand-col">
        <div className="fx-auth-brand-inner">
          <div>
            <span className="fx-auth-brand-eyebrow">
              <span className="dot" />
              {t("auth.brandColumn.login.eyebrow", "Mientras estuviste afuera")}
            </span>
            <p className="fx-auth-brand-quote">
              {t("auth.brandColumn.login.quoteLead", "Fiscalo emitió")}{" "}
              <span className="accent">
                {t("auth.brandColumn.login.quoteAccent1", "23 comprobantes")}
              </span>
              ,<br />
              {t("auth.brandColumn.login.quoteMid", "actualizó tu IVA y")}{" "}
              <span className="accent">
                {t("auth.brandColumn.login.quoteAccent2", "te lo dejó hecho.")}
              </span>
            </p>
          </div>

          <div className="fx-auth-panel-mini">
            <div className="header">
              <span>
                {t("auth.brandColumn.login.activityTitle", "Actividad reciente")}
              </span>
              <span className="live">
                {t("auth.brandColumn.login.live", "En vivo")}
              </span>
            </div>
            <ul>
              <li>
                <span className="ico">
                  <Receipt aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.login.activity1",
                    "e-Factura emitida · Cliente recurrente",
                  )}
                </span>
                <span className="when">
                  {t("auth.brandColumn.login.activity1When", "hace 12m")}
                </span>
              </li>
              <li>
                <span className="ico">
                  <RefreshCw aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.login.activity2",
                    "23 comprobantes sincronizados con DGI",
                  )}
                </span>
                <span className="when">
                  {t("auth.brandColumn.login.activity2When", "hace 1h")}
                </span>
              </li>
              <li>
                <span className="ico">
                  <Check aria-hidden />
                </span>
                <span>
                  {t(
                    "auth.brandColumn.login.activity3",
                    "IVA estimado del mes actualizado",
                  )}
                </span>
                <span className="when">
                  {t("auth.brandColumn.login.activity3When", "esta mañana")}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // mode === "register" && audience === "business"
  return (
    <div className="fx-auth-brand-col">
      <div className="fx-auth-brand-inner">
        <div>
          <span className="fx-auth-brand-eyebrow">
            <span className="dot" />
            {t("auth.brandColumn.register.eyebrow", "Por qué la red Fiscalo")}
          </span>
          <p className="fx-auth-brand-quote">
            “{t("auth.brandColumn.register.quoteLead", "Me sacó el peso de encima.")}
            <br />
            <span className="accent">
              {t("auth.brandColumn.register.quoteAccent", "Delego lo fiscal")}
            </span>{" "}
            {t(
              "auth.brandColumn.register.quoteTail",
              "y solo entro a DGI cuando me avisan.",
            )}
            ”
          </p>
          <div className="fx-auth-brand-attr">
            <span className="avatar">RV</span>
            <div className="who">
              <div className="name">Rodrigo Viera</div>
              <div className="role">COO · Salkantai</div>
            </div>
          </div>
        </div>

        <div className="fx-auth-panel-mini">
          <div className="header">
            <span>{t("auth.brandColumn.register.planTitle", "En el plan Tala")}</span>
            <span style={{ color: "var(--fx-accent-300)" }}>
              {t("auth.brandColumn.register.planFree", "Gratis")}
            </span>
          </div>
          <ul>
            <li>
              <span className="ico"><Check aria-hidden /></span>
              <span>
                {t("auth.brandColumn.register.plan1", "Hasta 5 comprobantes/mes")}
              </span>
              <span className="when" />
            </li>
            <li>
              <span className="ico"><Check aria-hidden /></span>
              <span>
                {t("auth.brandColumn.register.plan2", "e-Ticket, e-Factura y notas")}
              </span>
              <span className="when" />
            </li>
            <li>
              <span className="ico"><Check aria-hidden /></span>
              <span>
                {t("auth.brandColumn.register.plan3", "PDF + XML descargables")}
              </span>
              <span className="when" />
            </li>
            <li>
              <span className="ico"><Check aria-hidden /></span>
              <span>
                {t("auth.brandColumn.register.plan4", "Sin tarjeta para arrancar")}
              </span>
              <span className="when" />
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
